/**
 * useUploadSession — Supabase Realtime hook for upload progress
 * 
 * Listens to big_data_datasets table (upload columns) via Supabase Realtime.
 * Provides live upload_status, uploaded_chunks, bytes_uploaded, upload_speed.
 * Used by BigDataUploader and BigDataHome to show real-time progress after reconnect.
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

export interface UploadSessionRealtime {
  id: string;
  fileName: string;
  fileSize: number;
  uploadId: string | null;
  s3Key: string;
  uploadStatus: string | null;
  totalChunks: number | null;
  uploadedChunks: number | null;
  bytesUploaded: number | null;
  uploadSpeed: number | null;
  completedParts: Array<{ PartNumber: number; ETag: string }>;
  uploadError: string | null;
  createdAt: string;
}

interface UseUploadSessionOptions {
  userId?: string;
  datasetId?: string;
}

export function useUploadSession(options: UseUploadSessionOptions = {}) {
  const [sessions, setSessions] = useState<UploadSessionRealtime[]>([]);
  const [loading, setLoading] = useState(true);

  // Map a Supabase row to our type
  const mapRow = useCallback((row: any): UploadSessionRealtime => ({
    id: row.id,
    fileName: row.file_name,
    fileSize: row.file_size,
    uploadId: row.upload_id,
    s3Key: row.s3_key,
    uploadStatus: row.upload_status,
    totalChunks: row.total_chunks,
    uploadedChunks: row.uploaded_chunks,
    bytesUploaded: row.bytes_uploaded,
    uploadSpeed: row.upload_speed,
    completedParts: row.completed_parts || [],
    uploadError: row.upload_error,
    createdAt: row.created_at,
  }), []);

  useEffect(() => {
    if (!options.userId && !options.datasetId) {
      setLoading(false);
      return;
    }

    // 1. Load initial data (only rows with upload activity)
    const loadInitial = async () => {
      try {
        setLoading(true);

        let query = supabase
          .from('big_data_datasets')
          .select('*')
          .order('created_at', { ascending: false });

        if (options.datasetId) {
          query = query.eq('id', options.datasetId);
        } else if (options.userId) {
          query = query.eq('user_id', options.userId);
          query = query.in('upload_status', ['uploading', 'paused', 'completing']);
        }

        const { data, error } = await query;

        if (error) {
          logger.error('[useUploadSession] Initial load error:', error);
        } else {
          setSessions((data || []).map(mapRow));
        }
      } catch (err) {
        logger.error('[useUploadSession] Load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadInitial();

    // 2. Subscribe to realtime updates (big_data_datasets already has Realtime enabled)
    const channelName = options.datasetId
      ? `upload_session_${options.datasetId}`
      : `upload_sessions_${options.userId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'big_data_datasets',
          filter: options.userId ? `user_id=eq.${options.userId}` : undefined,
        },
        (payload) => {
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;

          // Only care about rows that have upload tracking data
          if (payload.eventType === 'INSERT' && newRecord.upload_status) {
            setSessions((prev) => [mapRow(newRecord), ...prev]);
          } else if (payload.eventType === 'UPDATE' && newRecord.upload_status) {
            setSessions((prev) => {
              const exists = prev.some((s) => s.id === newRecord.id);
              if (exists) {
                return prev.map((s) => (s.id === newRecord.id ? mapRow(newRecord) : s));
              }
              // If it just got an upload_status, add it
              return [mapRow(newRecord), ...prev];
            });
          } else if (payload.eventType === 'DELETE') {
            setSessions((prev) => prev.filter((s) => s.id !== oldRecord.id));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('[useUploadSession] Subscribed to realtime updates');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [options.userId, options.datasetId, mapRow]);

  // Filter helpers
  const activeUploads = sessions.filter(
    (s) => s.uploadStatus === 'uploading' || s.uploadStatus === 'paused'
  );

  const hasActiveUploads = activeUploads.length > 0;

  return {
    sessions,
    activeUploads,
    hasActiveUploads,
    loading,
  };
}

export default useUploadSession;
